import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { SiteService } from '@data-access/services/site.service';
import { Site } from '@core/models/site.model';

@Component({
  selector: 'app-site-selection-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, ModalComponent],
  template: `
    <app-modal
      [isOpen]="isOpen"
      [title]="'SITE_SELECTOR.TITLE' | translate"
      [subtitle]="'SITE_SELECTOR.SUBTITLE' | translate"
      [iconType]="'info'"
      [size]="'lg'"
      [showCloseButton]="allowDismiss"
      [closeOnBackdropClick]="allowDismiss"
      [primaryButtonText]="'COMMON.BUTTON.CONFIRM' | translate"
      [secondaryButtonText]="'COMMON.BUTTON.CANCEL' | translate"
      [showPrimaryButton]="true"
      [showSecondaryButton]="allowDismiss"
      [primaryButtonClass]="selectedSite ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
      (close)="onCancel()"
      (primaryAction)="onConfirm()"
      (secondaryAction)="onCancel()">

      <!-- Loading State -->
      <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
        <p class="text-gray-600">{{ 'COMMON.MESSAGE.LOADING' | translate }}</p>
      </div>

      <!-- Error State -->
      <div *ngIf="error && !isLoading" class="text-center py-8">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </div>
        <p class="text-red-600 mb-4">{{ 'SITE_SELECTOR.ERROR_LOADING' | translate }}</p>
        <button
          (click)="retryLoadSites()"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
          {{ 'COMMON.BUTTON.RETRY' | translate }}
        </button>
      </div>

      <!-- Empty State -->
      <div *ngIf="!isLoading && !error && sites.length === 0" class="text-center py-8">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
          <svg class="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
        </div>
        <p class="text-gray-600">{{ 'SITE_SELECTOR.NO_SITES' | translate }}</p>
      </div>

      <!-- Sites List -->
      <div *ngIf="!isLoading && !error && sites.length > 0" class="space-y-3 max-h-96 overflow-y-auto">
        <div *ngFor="let site of sites"
             (click)="selectSiteOption(site)"
             [class.border-purple-600]="selectedSite?.identifier === site.identifier"
             [class.bg-purple-50]="selectedSite?.identifier === site.identifier"
             class="border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:border-purple-400 hover:shadow-md transition-all duration-200">
          <div class="flex items-start">
            <!-- Radio Button -->
            <div class="flex items-center h-5 mt-1">
              <input
                type="radio"
                [checked]="selectedSite?.identifier === site.identifier"
                [id]="'site-' + site.identifier"
                name="site-selection"
                class="h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500 cursor-pointer"
                (click)="$event.stopPropagation()">
            </div>

            <!-- Site Info -->
            <div class="ml-3 flex-1">
              <label
                [for]="'site-' + site.identifier"
                class="block text-sm font-medium text-gray-900 cursor-pointer">
                {{ site.label }}
              </label>
              <p *ngIf="site.address" class="text-sm text-gray-600 mt-1">
                <svg class="inline-block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                {{ site.address }}
              </p>
              <p *ngIf="site.description" class="text-xs text-gray-500 mt-1">
                {{ site.description }}
              </p>
            </div>

            <!-- Checkmark for Selected -->
            <div *ngIf="selectedSite?.identifier === site.identifier" class="ml-3 flex-shrink-0">
              <svg class="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Required Message -->
      <div *ngIf="!allowDismiss && !isLoading && sites.length > 0" class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div class="flex">
          <svg class="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-sm text-blue-700">
            {{ 'SITE_SELECTOR.REQUIRED' | translate }}
          </p>
        </div>
      </div>
    </app-modal>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class SiteSelectionModalComponent implements OnInit, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() allowDismiss: boolean = true;

  @Output() siteSelected = new EventEmitter<Site>();
  @Output() cancelled = new EventEmitter<void>();

  sites: Site[] = [];
  selectedSite: Site | null = null;
  isLoading: boolean = false;
  error: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(private siteService: SiteService) {}

  ngOnInit(): void {
    // Subscribe to sites
    this.siteService.sites$
      .pipe(takeUntil(this.destroy$))
      .subscribe(sites => {
        this.sites = sites;
        this.isLoading = false;
      });

    // Load sites if modal is open
    if (this.isOpen) {
      this.loadSites();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadSites(): void {
    this.isLoading = true;
    this.error = null;
    try {
      this.siteService.loadSites();
    } catch (err) {
      this.error = 'Error loading sites';
      this.isLoading = false;
    }
  }

  retryLoadSites(): void {
    this.loadSites();
  }

  selectSiteOption(site: Site): void {
    this.selectedSite = site;
  }

  onConfirm(): void {
    if (this.selectedSite) {
      this.siteSelected.emit(this.selectedSite);
      this.selectedSite = null; // Reset selection
    }
  }

  onCancel(): void {
    if (this.allowDismiss) {
      this.cancelled.emit();
      this.selectedSite = null; // Reset selection
    }
  }
}
